import { useCallback, useEffect, useMemo, useState } from "react";
import { Environment, EnvironmentType, environmentTypesOptions } from "../../models/Environment"
import { Button, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { Form, Input, InputNumber, Select, Flex, Col, Avatar } from "antd";
import TextArea from "antd/es/input/TextArea";
import { FormProps, Rule } from "antd/es/form";
import ScriptEditor from "../common/ScriptEditor";
import { accessLevelOptions } from "../../utils";
import { debounce } from "lodash";
import './AssetEditForm.scss';

type Props = {
  environmentVariable?: Environment;
  onSubmit?: (environment: Environment) => void;
  onCancel?: () => void;
}

const formLayout: FormProps = {
  layout: "vertical",
  colon: false,
}

export default function EnvironmentVariableForm({ environmentVariable, onSubmit, onCancel }: Props) {
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState('');

  useEffect(() => {
    form.setFieldsValue(environmentVariable);
    if (environmentVariable && environmentVariable.type === EnvironmentType.Number) {
      form.setFieldValue("value", +environmentVariable.value);
    }
    if (environmentVariable && environmentVariable.img && environmentVariable.img !== "") {
      setImageUrl(environmentVariable.img);
    }
  }, [environmentVariable, form]);

  const renderInputComponent = useCallback((type: string) => {
    switch (type) {
      case 'number':
        return <InputNumber className="w-100" min={1} placeholder="Number" />;
      case 'string':
        return <Input placeholder="String" />;
      case 'email':
        return <Input type="email" placeholder="Email" />;
      case 'token':
        return <TextArea cols={24} rows={5} placeholder="Token" />;
      default:
        return <Input placeholder="Default" />;
    }
  }, []);

  const getInputRules = useCallback((type: string): Rule[] => {
    switch (type) {
      case 'number':
        return [{ min: 1, type: 'number', message: 'Invalid Number' }];
      case 'string':
        return [{ type: 'string', message: 'Invalid String' }];
      case 'email':
        return [{ type: 'email', message: 'Invalid Email' }];
      default:
        return [{ type: 'string', message: 'Invalid String' }];
    }
  }, []);

  const handleScriptChange = useCallback((value?: string) => form.setFieldValue('value', value), [form]);
  const handleImageChange = useCallback((evt: any) => setImageUrl(evt.target.value), []);
  const debounceImageChange = useMemo(() => debounce(handleImageChange, 500), [handleImageChange]);

  useEffect(() => {
    return () => {
      debounceImageChange.cancel();
    }
  }, [debounceImageChange]);

  return (
    <Form className="compact-form" form={form} {...formLayout} onFinish={onSubmit}>
      <Modal.Body>
        <Flex align="center">
          <Col span={6} className="text-center pe-2">
            <Avatar
              src={imageUrl}
              icon={<FontAwesomeIcon icon={solid('camera')} size="xl" />}
              className="border"
              style={{ height: 80, width: 80 }}
            />
          </Col>
          <Col flex="auto">
            <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Environment Variable Key is required' }]}>
              <Input placeholder="Variable name" />
            </Form.Item>
            <Form.Item name="img" label="Image">
              <Input onChange={debounceImageChange} placeholder="Variable image url" />
            </Form.Item>
          </Col>
        </Flex>

        <Form.Item name="description" label="Description">
          <TextArea cols={24} rows={5} placeholder="Variable Description" />
        </Form.Item>

        <Form.Item name="type" label="Type" rules={[{ required: true, message: 'Environment Variable Type is required' }]}>
          <Select placeholder="Variable Type" options={environmentTypesOptions} />
        </Form.Item>
        <Form.Item noStyle dependencies={["type"]}>
          {({ getFieldValue }) => {
            const variableType = getFieldValue("type");
            if (!variableType) return;

            return variableType !== 'json'
              ? (
                <Form.Item name="value" label="Value" rules={getInputRules(variableType)}>
                  {renderInputComponent(variableType)}
                </Form.Item>
              )
              : (
                <Form.Item label="Value">
                  <ScriptEditor
                    height={150}
                    value={getFieldValue("value")}
                    onChange={handleScriptChange}
                  />
                </Form.Item>
              );
          }}
        </Form.Item>

        <Form.Item
          name="accessLevel"
          label="Access Level"
          rules={[{ required: true, message: 'Environment Variable Access Level is required' }]}
        >
          <Select placeholder="Select privacy level" allowClear className="w-100">
            {accessLevelOptions.map((option, index) => (
              <Select.Option key={index} value={option.value} label={option.label}>
                <FontAwesomeIcon icon={option.icon} fixedWidth />
                <span className="ms-2">{option.label}</span>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="tags" label="Tags">
          <Select mode="tags" className="w-100" placeholder="Variable Tags" />
        </Form.Item>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <Button type="button" variant="light" size="sm" className="border" onClick={onCancel}>Close</Button>
        <Button type="submit" variant="success" size="sm">
          <FontAwesomeIcon icon={solid('save')} />
          <span className="ms-2">Save</span>
        </Button>
      </Modal.Footer>
    </Form>
  );
}